import React, { useRef, useEffect, useState } from "react";

export function SeamlessAutoScroll({
    items = [],
    speed = 60,
    gap = 20,
    titleKey = "name",
    imageKey = "image",
    widthKey = "w",
    heightKey = "h",
    imageWidth = null,
    imageHeight = null,
    defaultImageWidth = 128,
    defaultImageHeight = 128,
}) {
    const containerRef = useRef(null);
    const innerRef = useRef(null);
    const styleRef = useRef(null);
    const [isPaused, setIsPaused] = useState(false);
    const [measureId, setMeasureId] = useState(0);

    const finalImgSize = (it) => {
        const compW = Number(imageWidth);
        const compH = Number(imageHeight);
        const hasCompW = Number.isFinite(compW) && compW > 0;
        const hasCompH = Number.isFinite(compH) && compH > 0;

        const itemW = it && Number(it[widthKey]);
        const itemH = it && Number(it[heightKey]);
        const hasItemW = Number.isFinite(itemW) && itemW > 0;
        const hasItemH = Number.isFinite(itemH) && itemH > 0;

        const w = hasCompW ? compW : hasItemW ? itemW : defaultImageWidth;
        const h = hasCompH ? compH : hasItemH ? itemH : defaultImageHeight;
        return { w, h };
    };

    const preloadImages = (urls) =>
        Promise.all(
            urls.map((src) =>
                new Promise((res) => {
                    const img = new Image();
                    img.src = src;
                    if (img.decode) {
                        img.decode().then(() => res()).catch(() => res());
                    } else {
                        img.onload = img.onerror = () => res();
                    }
                })
            )
        );

    useEffect(() => {
        let mounted = true;

        const measureAndApply = async () => {
            const inner = innerRef.current;
            if (!inner || !containerRef.current) return;

            if (document.fonts && document.fonts.ready) {
                try { await document.fonts.ready; } catch (e) { }
            }

            const urls = items.map((it) => it && it[imageKey]).filter(Boolean);
            await preloadImages(urls);
            if (!mounted) return;
            await new Promise((r) => requestAnimationFrame(r));

            // Measure first copy children (0..items.length-1)
            const allChildren = Array.from(inner.querySelectorAll(".marquee-item"));
            const firstCopy = allChildren.slice(0, items.length);

            const measuredWidths = [];

            if (firstCopy.length === items.length) {
                // measure each child naturally
                for (let i = 0; i < firstCopy.length; i++) {
                    const child = firstCopy[i];
                    // clear any previously forced flex so natural width is measured
                    child.style.flex = "";
                    const w = Math.max(1, Math.round(child.offsetWidth));
                    measuredWidths.push(w);
                }
            } else {
                // fallback estimate
                for (let i = 0; i < items.length; i++) {
                    const it = items[i] || {};
                    const { w: imgW } = finalImgSize(it);
                    const title = it[titleKey] ? String(it[titleKey]) : "";
                    const estText = title ? Math.min(Math.max(60, title.length * 8), 160) : 0;
                    measuredWidths.push(Math.round(imgW + estText));
                }
            }

            // Force same widths onto ALL children (both copies) so layout matches measurement exactly
            for (let i = 0; i < allChildren.length; i++) {
                const copyIndex = i % items.length;
                const w = measuredWidths[copyIndex] || 1;
                allChildren[i].style.flex = `0 0 ${w}px`;
                allChildren[i].style.maxWidth = `${w}px`;
            }

            // Compute copyWidth: sum widths + gaps BETWEEN items, but NO trailing gap after last item.
            const sumWidths = measuredWidths.reduce((s, v) => s + v, 0);
            const totalGaps = Math.max(0, items.length - 1) * gap; // gaps between items only
            const copyWidth = Math.max(1, Math.round(sumWidths + totalGaps));

            // Set inner width to exactly two copies (no extra)
            inner.style.width = `${copyWidth * 2}px`;
            inner.style.boxSizing = "border-box";

            // Create and inject unique keyframes to animate exactly -copyWidth
            const durationSec = Math.max(0.01, copyWidth / Math.max(1, speed));
            const unique = `marqueeLoop_${Math.random().toString(36).slice(2, 9)}`;
            const keyframes = `
        @keyframes ${unique} {
          from { transform: translate3d(0,0,0); }
          to   { transform: translate3d(-${copyWidth}px,0,0); }
        }
      `;
            if (styleRef.current) {
                styleRef.current.textContent = keyframes;
            } else {
                const s = document.createElement("style");
                s.type = "text/css";
                s.textContent = keyframes;
                document.head.appendChild(s);
                styleRef.current = s;
            }

            inner.style.animation = `${unique} ${durationSec}s linear infinite`;
            inner.style.animationPlayState = isPaused ? "paused" : "running";
            inner.style.willChange = "transform";
            inner.style.transform = "translateZ(0)";
        };

        measureAndApply();
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, speed, gap, measureId, imageWidth, imageHeight]);

    // pause on hover
    useEffect(() => {
        if (innerRef.current) innerRef.current.style.animationPlayState = isPaused ? "paused" : "running";
    }, [isPaused]);

    // resize -> re-measure
    useEffect(() => {
        let tid = null;
        const onResize = () => {
            clearTimeout(tid);
            tid = setTimeout(() => setMeasureId((n) => n + 1), 150);
        };
        window.addEventListener("resize", onResize);
        return () => { window.removeEventListener("resize", onResize); clearTimeout(tid); };
    }, []);

    // cleanup
    useEffect(() => {
        return () => {
            if (styleRef.current) {
                try { styleRef.current.remove(); } catch (e) { }
                styleRef.current = null;
            }
        };
    }, []);

    const displayed = [...items, ...items];

    return (
        <div
            ref={containerRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            // IMPORTANT: do NOT center the inner; keep it left-aligned to avoid offsetting
            className="w-full overflow-hidden font-urbanist py-8"
            style={{ position: "relative", display: "block" }}
        >
            <div
                ref={innerRef}
                className="flex marquee-inner"
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: `${gap}px`, // gap between items (we accounted for gaps when computing copyWidth)
                    margin: 0,
                    padding: 0,
                }}
            >
                {displayed.map((item, idx) => {
                    const it = item || {};
                    const title = it[titleKey];
                    const src = it[imageKey] || "";
                    const { w: imgW, h: imgH } = finalImgSize(it);

                    return (
                        <div
                            key={`${src || "img"}-${idx}`}
                            className="marquee-item"
                            style={{
                                display: "inline-flex",
                                flex: "0 0 auto", // natural until measurement forces widths
                                alignItems: "center",
                                boxSizing: "border-box",
                                gap: 8,
                                padding: 0,
                            }}
                        >
                            <img
                                src={src}
                                alt={title || ""}
                                width={imgW}
                                height={imgH}
                                style={{
                                    width: imgW,
                                    height: imgH,
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                    display: "block",
                                    marginBottom: 8,
                                    border: "2px solid currentColor",
                                }}
                            />
                            {title ? (
                                <div style={{ color: "white", textAlign: "center", padding: "0 8px", maxWidth: Math.max(60, imgW) }}>
                                    {title}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


// export default function SeamlessAutoScroll({
//     items = [],
//     speed = 60,
//     gap = 20,
//     itemWidth = null, // optional fixed width per item (px). If provided, measurement is skipped.
// }) {
//     const containerRef = useRef(null);
//     const innerRef = useRef(null);
//     const styleRef = useRef(null);
//     const [ready, setReady] = useState(false);
//     const [animName, setAnimName] = useState(null);
//     const [isPaused, setIsPaused] = useState(false);
//     const [measureId, setMeasureId] = useState(0); // bump to retrigger measure on resize

//     // Preload & decode images, returns a promise
//     const preloadImages = (urls) =>
//         Promise.all(
//             urls.map((src) =>
//                 new Promise((res) => {
//                     const img = new Image();
//                     img.src = src;
//                     // Use decode if available for better reliability
//                     if (img.decode) {
//                         img.decode().then(() => res()).catch(() => res());
//                     } else {
//                         img.onload = img.onerror = () => res();
//                     }
//                 })
//             )
//         );

//     // measure widths after fonts and images loaded
//     useEffect(() => {
//         let mounted = true;
//         const measure = async () => {
//             if (!innerRef.current || !containerRef.current) return;

//             // wait for fonts to load to avoid font-caused reflow
//             if (document.fonts && document.fonts.ready) {
//                 try {
//                     await document.fonts.ready;
//                 } catch (e) {
//                     /* ignore */
//                 }
//             }

//             // preload images (only first copy)
//             const urls = items.map((it) => it.image);
//             await preloadImages(urls);

//             if (!mounted) return;

//             // Give browser one rAF to settle layout before measuring
//             await new Promise((r) => requestAnimationFrame(r));

//             const inner = innerRef.current;
//             const children = Array.from(inner.querySelectorAll(".marquee-item:first-child, .marquee-item")).slice(0, items.length);
//             let total = 0;

//             // if user provided fixed width, use that
//             if (itemWidth && Number.isFinite(itemWidth) && itemWidth > 0) {
//                 total = items.length * itemWidth + (items.length - 1) * gap;
//             } else {
//                 // measure each child bounding box width (including margin/gap manually)
//                 for (let i = 0; i < items.length; i++) {
//                     const child = inner.querySelectorAll(".marquee-item")[i];
//                     if (!child) continue;
//                     const rect = child.getBoundingClientRect();
//                     // use offsetWidth (more robust)
//                     const w = Math.round(child.offsetWidth);
//                     total += w;
//                     if (i !== items.length - 1) total += gap;
//                 }
//             }

//             // small overlap buffer to avoid exact-equality rounding blink
//             const overlapBuffer = 0.5;
//             const copyWidth = Math.max(1, total - 0 + overlapBuffer);

//             // create keyframes
//             const durationSec = Math.max(0.01, copyWidth / Math.max(1, speed));
//             const unique = `marqueeAnim_${Math.random().toString(36).slice(2, 10)}`;
//             const keyframes = `
//         @keyframes ${unique} {
//           from { transform: translate3d(0,0,0); }
//           to   { transform: translate3d(-${copyWidth}px,0,0); }
//         }
//       `;

//             // inject or replace style node
//             if (styleRef.current) {
//                 styleRef.current.textContent = keyframes;
//             } else {
//                 const s = document.createElement("style");
//                 s.type = "text/css";
//                 s.textContent = keyframes;
//                 document.head.appendChild(s);
//                 styleRef.current = s;
//             }

//             // set inner container width exactly (two copies)
//             inner.style.width = `${copyWidth * 2}px`;
//             // ensure the direct children layout uses flex basis we measured (if itemWidth used)
//             // apply inline style for animation
//             inner.style.animation = `${unique} ${durationSec}s linear infinite`;
//             inner.style.animationPlayState = isPaused ? "paused" : "running";
//             inner.style.willChange = "transform";
//             inner.style.transform = "translateZ(0)";

//             setAnimName(unique);
//             setReady(true);
//         };

//         measure();

//         return () => {
//             mounted = false;
//         };
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [items, speed, gap, itemWidth, measureId]);

//     // pause / resume on hover
//     useEffect(() => {
//         if (!innerRef.current) return;
//         innerRef.current.style.animationPlayState = isPaused ? "paused" : "running";
//     }, [isPaused]);

//     // handle window resize -> re-measure (debounced)
//     useEffect(() => {
//         let t = null;
//         const onResize = () => {
//             clearTimeout(t);
//             t = setTimeout(() => {
//                 // bump measureId to retrigger effect
//                 setReady(false);
//                 setMeasureId((n) => n + 1);
//             }, 150);
//         };
//         window.addEventListener("resize", onResize);
//         return () => {
//             window.removeEventListener("resize", onResize);
//             clearTimeout(t);
//         };
//     }, []);

//     // cleanup style tag on unmount
//     useEffect(() => {
//         return () => {
//             if (styleRef.current) {
//                 try {
//                     styleRef.current.remove();
//                 } catch (e) { }
//                 styleRef.current = null;
//             }
//         };
//     }, []);

//     // duplicated display
//     const displayed = [...items, ...items];

//     return (
//         <div
//             ref={containerRef}
//             onMouseEnter={() => setIsPaused(true)}
//             onMouseLeave={() => setIsPaused(false)}
//             style={{
//                 width: "100%",
//                 overflow: "hidden",
//                 position: "relative",
//             }}
//         >
//             <div
//                 ref={innerRef}
//                 className="marquee-inner"
//                 style={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: `${gap}px`,
//                     // width will be set after measuring
//                 }}
//             >
//                 {displayed.map((it, idx) => (
//                     <div
//                         key={`${it.name}-${idx}`}
//                         className="marquee-item"
//                         style={{
//                             display: "inline-flex",
//                             flexDirection: 'column',
//                             justifyContent: 'center',
//                             alignItems: "center",
//                             flex: "0 0 auto",
//                             boxSizing: "border-box",
//                             // fix height to avoid layout shift
//                             padding: 0,
//                         }}
//                     >
//                         <img
//                             src={it.image}
//                             alt={it.name}
//                             width={128}
//                             height={128}
//                             style={{
//                                 width: 128,
//                                 height: 128,
//                                 objectFit: "cover",
//                                 display: "block",
//                                 borderRadius: "50%",
//                                 backfaceVisibility: "hidden",
//                                 transform: "translateZ(0)",
//                             }}
//                         />
//                         <div style={{ color: "white", textAlign: "center", padding: "0 8px" }}>{it.name}</div>
//                     </div>
//                 ))}
//             </div>
//         </div>
//     );
// }